import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap,
  Leaf,
  DollarSign,
  Users,
  Globe,
  Shield,
  ArrowRight,
  BarChart3,
  Clock,
  MapPin,
  Star
} from 'lucide-react';

// AnimatedMetricValue component for animating numbers
const AnimatedMetricValue: React.FC<{ target: number; unit: string; duration?: number; precision?: number }> = ({
  target,
  unit,
  duration = 2000, // default 2 seconds
  precision = 0, // default 0 decimal places
}) => {
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    let start: number | null = null;
    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const percentage = Math.min(progress / duration, 1);
      const newValue = target * percentage;
      setCurrentValue(newValue);

      if (progress < duration) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(target);
      }
    };

    requestAnimationFrame(animate);

    return () => {
      start = null;
    };
  }, [target, duration]);

  // Format the number based on precision
  const formattedValue = currentValue.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return (
    <>
      {formattedValue}
      {unit}
    </>
  );
};

export default function Hero() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [animatedAmount, setAnimatedAmount] = useState(0);
  const [investmentAmount, setInvestmentAmount] = useState(10000);
  const [statsVisible, setStatsVisible] = useState(false);
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);

  // Calculator calculations based on real project data
  const calculateReturns = (amount: number) => {
    // Based on actual Greenova8 project returns: 24-28% annually
    const annualReturnRate = 0.26; // 26% average
    return Math.round(amount * annualReturnRate);
  };

  const calculateCO2Offset = (amount: number) => {
    // Based on real data: ~0.12 tons CO2 per Rs.10,000 invested
    const co2Per10k = 0.12;
    return (amount / 10000) * co2Per10k;
  };

  const calculateEnergyProduced = (amount: number) => {
    // Based on real data: ~100 kWh per Rs.10,000 invested annually
    const kwhPer10k = 100;
    return (amount / 10000) * kwhPer10k;
  };

  const calculateTreesEquivalent = (co2Tons: number) => {
    // 1 tree absorbs ~0.036 tons CO2 per year
    return Math.round(co2Tons / 0.036);
  };

  const calculateHomesPowered = (kwh: number) => {
    // Average Pakistani home uses ~30 kWh per month = 360 kWh per year
    return Math.round(kwh / 360);
  };

  const handleInvestmentChange = (value: number) => {
    setInvestmentAmount(value);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Scroll detection for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStatsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const statsElement = document.getElementById('stats-section');
    if (statsElement) {
      observer.observe(statsElement);
    }

    return () => {
      if (statsElement) {
        observer.unobserve(statsElement);
      }
    };
  }, []);

  // Animate stats when visible
  useEffect(() => {
    if (statsVisible) {
      const duration = 3000; // 3 seconds for more moderate pace

      stats.forEach((stat, index) => {
        const target = stat.target;
        const startTime = Date.now();

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function for smooth animation (ease-out)
          const easeOut = 1 - Math.pow(1 - progress, 3);
          const currentValue = target * easeOut;

          setAnimatedStats(prev => {
            const newStats = [...prev];
            newStats[index] = currentValue;
            return newStats;
          });

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Ensure final value is exactly the target
            setAnimatedStats(prev => {
              const newStats = [...prev];
              newStats[index] = target;
              return newStats;
            });
          }
        };

        requestAnimationFrame(animate);
      });
    }
  }, [statsVisible]);

  // Animate the amount counter
  useEffect(() => {
    const targetAmount = 30000;
    const duration = 2000; // 2 seconds
    const steps = 60; // 60 steps for smooth animation
    const increment = targetAmount / steps;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const newAmount = Math.min(Math.floor(increment * currentStep), targetAmount);
      setAnimatedAmount(newAmount);

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedAmount(targetAmount);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const stats = [
    {
      label: "Total Invested",
      value: "Rs.26.0M",
      target: 26.0,
      unit: "M",
      prefix: "Rs.",
      icon: DollarSign,
      change: "Verified by SECP Register",
      color: "text-green-600"
    },
    {
      label: "Active Investors",
      value: "1,540+",
      target: 1540,
      unit: "+",
      prefix: "",
      icon: Users,
      change: "Verified by Blockchain Ledger",
      color: "text-blue-600"
    },
    {
      label: "Tons of CO2 Saved",
      value: "3,851 tons",
      target: 3851,
      unit: " tons",
      prefix: "",
      icon: Leaf,
      change: "Verified by Carbon Registry",
      color: "text-purple-600"
    },
    {
      label: "All Transactions Compliant & Audited",
      value: "100%",
      target: 100,
      unit: "%",
      prefix: "",
      icon: Shield,
      change: "Verified by SBP & SECP",
      color: "text-green-600"
    },
  ];

  const realTimeMetrics = [
    { label: "Current Power Output", target: 5420, unit: " kW", trend: "up", precision: 0 },
    { label: "Wind Speed", target: 12.3, unit: " m/s", trend: "up", precision: 1 },
    { label: "Efficiency", target: 94.2, unit: "%", trend: "up", precision: 1 },
    { label: "Uptime", target: 99.8, unit: "%", trend: "stable", precision: 1 }
  ];

  const investmentOpportunities = [
    {
      title: "Solar Farm Karachi",
      capacity: "50 MW",
      investment: "$100 - $10,000",
      returns: "12-15% APY",
      status: "Active",
      progress: 75,
      investors: 1247,
      location: "Karachi, Pakistan"
    },
    {
      title: "Wind Energy Hub",
      capacity: "30 MW",
      investment: "$200 - $5,000",
      returns: "10-12% APY",
      status: "Active",
      progress: 60,
      investors: 892,
      location: "Sindh Province"
    },
    {
      title: "Community Solar",
      capacity: "10 MW",
      investment: "$50 - $1,000",
      returns: "8-10% APY",
      status: "Active",
      progress: 45,
      investors: 2156,
      location: "Lahore, Pakistan"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Bank-Level Security",
      description: "Your investments are protected with enterprise-grade security"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Track your environmental impact in real-time"
    },
    {
      icon: BarChart3,
      title: "Transparent Returns",
      description: "Real-time tracking of your investment performance"
    },
    {
      icon: Clock,
      title: "24/7 Monitoring",
      description: "Continuous monitoring of all energy projects"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Ahmed",
      role: "Investor",
      content: "Made $2,400 returns in just 6 months!",
      rating: 5
    },
    {
      name: "Hassan Malik",
      role: "Tech Entrepreneur",
      content: "Transparent and secure platform.",
      rating: 5
    },
    {
      name: "Fatima Javed",
      role: "Small Business Owner",
      content: "Easy to understand and invest.",
      rating: 4
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gray-50">
      {/* Hero Content with Video Background */}
      <div className="relative flex items-center min-h-[100vh] px-4 pt-16">
        {/* Video Background - Contained within hero section */}
        <div className="absolute inset-0">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/assets/videos/211989_medium.mp4" type="video/mp4" />
            {/* Fallback image if video doesn't load */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/assets/images/wind-turbine-sunset.jpg')`,
                filter: 'brightness(0.8) contrast(1.1)'
              }}
            ></div>
          </video>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/10 via-transparent to-blue-900/10"></div>
        </div>
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <motion.div
            className="text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Live Status Indicator */}
            <motion.div
              className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-8"
              variants={itemVariants}
            >
              <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
              <span>Live Energy Production: 5,420 kW</span>
              <span className="text-green-200">•</span>
              <span>{currentTime.toLocaleTimeString()}</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              variants={itemVariants}
            >
              <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent drop-shadow-2xl">
                The Climate Investment Platform for
              </span>
              <br />
              <span className="bg-gradient-to-r from-green-400 via-green-500 to-green-400 bg-clip-text text-transparent drop-shadow-2xl">
                Everyone
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed font-medium"
              variants={itemVariants}
            >
              <span className="bg-gradient-to-r from-gray-200 via-white to-gray-200 bg-clip-text text-transparent drop-shadow-lg">
                Projected 24-28% annual returns, backed by audited solar & wind projects.
                Starting at Rs{animatedAmount.toLocaleString()}.
              </span>
            </motion.p>

            {/* Real-time Metrics */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto"
              variants={itemVariants}
            >
              {realTimeMetrics.map((metric, index) => (
                <div key={index} className="bg-white/90 backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-xl hover:bg-white/95 transition-all duration-300">
                  <div className="text-3xl font-black text-gray-900 mb-2">
                    <AnimatedMetricValue
                      target={metric.target}
                      unit={metric.unit}
                      precision={metric.precision}
                    />
                  </div>
                  <div className="text-sm text-gray-700 font-semibold mb-2">{metric.label}</div>
                  <div className={`text-xs font-bold ${metric.trend === 'up' ? 'text-green-600' :
                    metric.trend === 'down' ? 'text-red-600' : 'text-blue-600'
                    }`}>
                    {metric.trend === 'up' ? '↗' : metric.trend === 'down' ? '↘' : '→'} Live
                  </div>
                </div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              className="flex justify-center items-center mb-16"
              variants={itemVariants}
            >
              <Link
                to="/invest"
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white px-10 py-5 rounded-xl text-xl font-bold transition-all duration-300 flex items-center space-x-3 shadow-2xl hover:shadow-green-500/25 transform hover:scale-105"
              >
                <span>Start Investing</span>
                <ArrowRight className="w-6 h-6" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Statistics Section */}
      <motion.div
        id="stats-section"
        className="relative z-10 py-20 bg-white"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Real Impact, Real Returns</h2>
            <p className="text-xl text-gray-600">Join thousands of investors making a difference</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center p-6 bg-gray-50 rounded-2xl"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${stat.color === 'text-green-600' ? 'bg-green-100' :
                    stat.color === 'text-blue-600' ? 'bg-blue-100' :
                      stat.color === 'text-purple-600' ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                    <Icon className={`w-8 h-8 ${stat.color}`} />
                  </div>
                  <div className={`text-3xl font-bold mb-2 ${stat.color}`}>
                    {stat.prefix}
                    {stat.unit === 'M' ? animatedStats[index].toFixed(1) :
                      stat.unit === '%' ? Math.round(animatedStats[index]) :
                        animatedStats[index].toLocaleString()}
                    {stat.unit}
                  </div>
                  <div className="text-gray-600 mb-2">{stat.label}</div>
                  <div className="text-sm text-green-600 font-medium">{stat.change}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* How It Works Section */}
      <motion.div
        className="relative z-10 py-20 bg-gray-50"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How Greenova8 Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform makes it easy to invest in renewable energy projects with blockchain security and complete transparency.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full px-6 py-2 shadow-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700">Step 4 of 4</span>
            </div>
          </div>

          {/* Steps Flow */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute top-16 left-0 right-0 h-0.5 bg-green-600 hidden lg:block"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  number: "01",
                  title: "Create Your Account",
                  description: "Quick digital KYC via NADRA",
                  subDescription: "Complete KYC in < 60 sec",
                  buttonText: "Secure & Fast Verification",
                  icon: Users,
                  color: "bg-green-600"
                },
                {
                  number: "02",
                  title: "Choose a Project",
                  description: "Browse verified solar, wind, hydro & more clean energy options",
                  subDescription: "Browse verified projects. Tap Invest.",
                  buttonText: "Curated Green Investments",
                  icon: Leaf,
                  color: "bg-blue-600"
                },
                {
                  number: "03",
                  title: "Fund via Blockchain",
                  description: "Invest from just $100 via bank transfer or mobile payment",
                  subDescription: "Use local payment options—Rs. or USD stablecoins",
                  buttonText: "Transparent & Secure",
                  icon: Shield,
                  color: "bg-green-600"
                },
                {
                  number: "04",
                  title: "Track ROI & Impact",
                  description: "Monitor 24-28% returns & CO₂ offset in real-time dashboard",
                  subDescription: "Real-time dashboard: earnings, carbon offset, homes powered",
                  buttonText: "Live Performance Tracking",
                  icon: BarChart3,
                  color: "bg-blue-600"
                }
              ].map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={index}
                    className="relative"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 + index * 0.1, duration: 0.6 }}
                  >
                    {/* Step Circle */}
                    <div className="flex justify-center mb-6">
                      <div className={`w-12 h-12 ${step.color} rounded-full flex items-center justify-center relative z-10`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Step Card */}
                    <motion.div
                      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
                      whileHover={{ y: -5, scale: 1.02 }}
                    >
                      <div className="text-6xl font-bold text-gray-200 mb-4">{step.number}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 mb-2 text-sm">{step.description}</p>
                      <p className="text-gray-500 mb-4 text-xs">{step.subDescription}</p>
                      <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                        {step.buttonText}
                      </button>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Protection Promise Section */}
      <motion.div
        className="relative z-10 py-20 bg-white"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Protection Promise</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Every dollar is traceable, auditable, and returnable based on real infrastructure-backed assets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Zap,
                title: "Blockchain-Secured",
                description: "Every transaction verified on blockchain",
                color: "bg-green-600"
              },
              {
                icon: Leaf,
                title: "Carbon Registry Verified",
                description: "Certified by international climate standards",
                color: "bg-green-600"
              },
              {
                icon: DollarSign,
                title: "Every PKR is Audited & Returnable",
                description: "Traceable, auditable, returnable assets",
                color: "bg-green-600"
              },
              {
                icon: BarChart3,
                title: "Infrastructure-Backed",
                description: "Real energy projects, real returns",
                color: "bg-green-600"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 + index * 0.1, duration: 0.6 }}
                >
                  <motion.div
                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    {/* Background Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Icon */}
                    <div className="flex justify-center mb-6 relative z-10">
                      <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors duration-300">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed group-hover:text-gray-700 transition-colors duration-300">{item.description}</p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Benefits Section */}
      <motion.div
        className="relative z-10 py-20 bg-gray-50"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Investing with <span className="text-green-600">Greenova8</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              By investing through our platform, you're not just growing your wealth you're helping build a sustainable future for Pakistan.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left Column - Benefits Cards */}
            <div className="space-y-6">
              {/* Financial Benefits Card */}
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 h-80"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.6, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Financial Benefits</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { text: "24-28% Annual Returns", icon: DollarSign, color: "text-yellow-600" },
                    { text: "Low Volatility", icon: Shield, color: "text-pink-600" },
                    { text: "Portfolio Diversification", icon: Star, color: "text-green-600" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div className="flex items-center">
                        <item.icon className={`w-4 h-4 ${item.color} mr-2`} />
                        <span className="text-gray-700 font-medium">{item.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Environmental Impact Card */}
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 h-80"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.8, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <Leaf className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Environmental Impact</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { text: "Carbon Offset", icon: Globe, color: "text-blue-600" },
                    { text: "Liquidity on Demand", icon: Shield, color: "text-orange-600" },
                    { text: "Infrastructure-backed Assets", icon: MapPin, color: "text-amber-600" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div className="flex items-center">
                        <item.icon className={`w-4 h-4 ${item.color} mr-2`} />
                        <span className="text-gray-700 font-medium">{item.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Column - Impact Calculator */}
            <motion.div
              className=""
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.0, duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 h-[680px] flex flex-col">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Impact Calculator</h3>
                <p className="text-gray-600 mb-6">Your Investment Amount (Rs.)</p>

                {/* Investment Slider */}
                <div className="mb-8">
                  <input
                    type="range"
                    min="10000"
                    max="500000"
                    step="5000"
                    value={investmentAmount}
                    onChange={(e) => handleInvestmentChange(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, #10b981 0%, #10b981 ${((investmentAmount - 10000) / (500000 - 10000)) * 100}%, #e5e7eb ${((investmentAmount - 10000) / (500000 - 10000)) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-sm text-gray-600">
                    <span>Rs.10,000</span>
                    <span>Rs.25,000</span>
                    <span>Rs.500,000</span>
                  </div>
                  <div className="text-center mt-2">
                    <span className="text-lg font-semibold text-green-600">
                      Rs.{investmentAmount.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Impact Results - Three boxes side by side */}
                <div className="grid grid-cols-3 gap-3 mb-8">
                  {/* Annual Return Box */}
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      Rs.{calculateReturns(investmentAmount).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">Annual Return</div>
                  </div>

                  {/* CO2 Offset Box */}
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-2xl font-bold text-green-600">
                      {calculateCO2Offset(investmentAmount).toFixed(2)} T
                    </div>
                    <div className="text-xs text-gray-600 mb-1">CO₂ Offset</div>
                    <div className="flex items-center justify-center text-green-600 text-xs">
                      <Leaf className="w-3 h-3 mr-1" />
                      = {calculateTreesEquivalent(calculateCO2Offset(investmentAmount))} trees
                    </div>
                  </div>

                  {/* Energy Produced Box */}
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {calculateEnergyProduced(investmentAmount).toLocaleString()} kWh
                    </div>
                    <div className="text-xs text-gray-600 mb-1">Energy Produced</div>
                    <div className="flex items-center justify-center text-gray-600 text-xs">
                      <Zap className="w-3 h-3 mr-1 text-red-500" />
                      Powers {calculateHomesPowered(calculateEnergyProduced(investmentAmount))} homes
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 mt-auto">
                  <button className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Compare to Fixed Deposit
                  </button>
                  <button className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Share My Impact
                  </button>
                  <button className="w-full py-4 px-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-bold text-lg">
                    Invest Now
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Investment Opportunities Section */}
      <motion.div
        className="relative z-10 py-20 bg-gray-50"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Active Investment Opportunities</h2>
            <p className="text-xl text-gray-600">Choose from our curated selection of clean energy projects</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {investmentOpportunities.map((project, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + index * 0.1, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="h-48 bg-gradient-to-br from-green-100 to-blue-100 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {project.status}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center text-gray-600 text-sm">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{project.capacity}</div>
                      <div className="text-xs text-gray-500">Capacity</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{project.returns}</div>
                      <div className="text-xs text-gray-500">Expected Returns</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Investment Range:</span>
                      <span className="font-semibold text-gray-900">{project.investment}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Progress</span>
                      <span>{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-600">
                      <Users className="w-4 h-4 inline mr-1" />
                      {project.investors} investors
                    </div>
                    <div className="flex items-center text-yellow-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm text-gray-600 ml-1">4.8</span>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors">
                      Invest Now
                    </button>
                    <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                      Details
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Features Section */}
      <motion.div
        className="relative z-10 py-20 bg-white"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose GreenOVA8?</h2>
            <p className="text-xl text-gray-600">Built for investors who want transparency and impact</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center p-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 + index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Testimonials Section */}
      <motion.div
        className="relative z-10 py-20 bg-gray-50"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Investors Say</h2>
            <p className="text-xl text-gray-600">Real stories from real investors</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.0 + index * 0.1, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Referral Program Section */}
      <motion.div
        className="relative z-10 py-20 bg-white"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2, duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4">
          {/* Referral Badge */}
          <div className="flex justify-center mb-8">
            <div className="bg-green-100 border border-green-200 rounded-full px-6 py-3 shadow-lg">
              <span className="text-green-700 font-semibold text-sm flex items-center">
                🎁 Referral Program
              </span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Earn <span className="text-green-600">Rs500</span> for Every Friend You Refer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Share the power of clean energy investing. You and your friend both get Rs500 when they make their first investment.
            </p>
          </div>

          {/* Referral Code Generator Card */}
          <motion.div
            className="bg-white rounded-2xl p-10 shadow-xl border border-gray-200 max-w-lg mx-auto relative overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.4, duration: 0.6 }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {/* Icon */}
            <div className="flex justify-center mb-8 relative z-10">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                <Users className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Card Title */}
            <div className="text-center mb-8 relative z-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-green-700 transition-colors duration-300">
                Get Your Referral Code
              </h3>
              <p className="text-lg text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                Enter your email to generate your unique referral code
              </p>
            </div>

            {/* Email Input */}
            <div className="mb-8 relative z-10">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Your Email Address
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 text-lg"
              />
            </div>

            {/* Generate Button */}
            <button className="w-full bg-green-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl relative z-10">
              Generate Referral Code
            </button>
          </motion.div>

          {/* Disclaimer */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-2 inline-block">
              * Bonus credited after your friend's first investment of Rs10,000 or more
            </p>
          </div>
        </div>
      </motion.div>

      {/* Call to Action Section */}
      <motion.div
        className="relative z-10 py-20 bg-green-600"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.6, duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Clean Energy Investment Journey?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Join 15,420+ investors who are already earning returns while building a sustainable future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/invest"
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Start Investing Now</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/learn"
              className="bg-green-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-800 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}